export type BrandOption = {
  name: string;
  nameEn?: string;
  nameCn?: string;
  count: number;
  recommendableCount?: number;
};

export type BrandDirectorySnapshot = {
  catalogVersion: number;
  brands: BrandOption[];
};

const STORAGE_KEY = "beautinow.productSearch.brandDirectory.v1";
let memorySnapshot: BrandDirectorySnapshot | null = null;
let inFlight: Promise<BrandDirectorySnapshot> | null = null;

export function normalizeBrandText(value: unknown): string {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\u3400-\u9fff]+/g, "");
}

export function parseBrandDirectory(value: unknown): BrandOption[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const brands: BrandOption[] = [];
  value.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const row = item as Partial<BrandOption>;
    const name = String(row.name || "").trim();
    const key = normalizeBrandText(name);
    if (!key || seen.has(key)) return;
    const count = Number(row.count || 0);
    const recommendableCount = Number(row.recommendableCount || 0);
    if (!Number.isFinite(count) || !Number.isFinite(recommendableCount)) return;
    seen.add(key);
    brands.push({
      name,
      nameEn: String(row.nameEn || "").trim() || undefined,
      nameCn: String(row.nameCn || "").trim() || undefined,
      count: Math.max(0, count),
      recommendableCount: Math.max(0, recommendableCount),
    });
  });
  return brands;
}

export function recommendableBrands(brands: BrandOption[]): BrandOption[] {
  return brands.filter((brand) => Number(brand.recommendableCount || 0) > 0);
}

export function browsableBrands(brands: BrandOption[]): BrandOption[] {
  return brands.filter((brand) => Boolean(brand.nameEn || brand.nameCn || Number(brand.recommendableCount || 0) > 0));
}

export function filterBrandOptions(brands: BrandOption[], query: string): BrandOption[] {
  const browsable = browsableBrands(brands);
  const needle = normalizeBrandText(query);
  if (!needle) return browsable;
  return browsable.filter((brand) => normalizeBrandText(`${brand.name} ${brand.nameEn || ""} ${brand.nameCn || ""}`).includes(needle));
}

export function brandInitial(brand: BrandOption): string {
  const label = String(brand.nameEn || brand.name || "").trim();
  const initial = label.charAt(0).toUpperCase();
  return /^[A-Z]$/.test(initial) ? initial : "#";
}

export function groupBrandOptions(brands: BrandOption[]): Array<{ initial: string; brands: BrandOption[] }> {
  const groups = new Map<string, BrandOption[]>();
  [...brands]
    .sort((left, right) => (left.nameEn || left.name).localeCompare(right.nameEn || right.name, "en", { sensitivity: "base" }))
    .forEach((brand) => {
      const initial = brandInitial(brand);
      groups.set(initial, [...(groups.get(initial) || []), brand]);
    });
  return [...groups.entries()]
    .sort(([left], [right]) => left === "#" ? 1 : right === "#" ? -1 : left.localeCompare(right))
    .map(([initial, groupedBrands]) => ({ initial, brands: groupedBrands }));
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function cachedBrandDirectory(): BrandDirectorySnapshot | null {
  if (memorySnapshot) return memorySnapshot;
  const target = storage();
  if (!target) return null;
  try {
    const value = JSON.parse(target.getItem(STORAGE_KEY) || "null") as Partial<BrandDirectorySnapshot> | null;
    const brands = parseBrandDirectory(value?.brands);
    const catalogVersion = Number(value?.catalogVersion || 0);
    if (!brands.length || !Number.isFinite(catalogVersion)) return null;
    memorySnapshot = { catalogVersion, brands };
    return memorySnapshot;
  } catch {
    return null;
  }
}

function saveBrandDirectory(snapshot: BrandDirectorySnapshot): BrandDirectorySnapshot {
  memorySnapshot = snapshot;
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // A restricted WebView may reject session storage. Memory caching still prevents page-to-page flicker.
  }
  return snapshot;
}

export function loadBrandDirectory(
  loader: () => Promise<{ catalogVersion?: number; brands?: unknown }>,
  force = false,
): Promise<BrandDirectorySnapshot> {
  if (!force && inFlight) return inFlight;
  inFlight = loader()
    .then((payload) => {
      const brands = parseBrandDirectory(payload?.brands);
      if (!brands.length) throw new Error("品牌目录为空");
      return saveBrandDirectory({ catalogVersion: Number(payload.catalogVersion || 0), brands });
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}
